// Returns true only when dimensions represent a valid square image.
export const canSaveSquareTemplate = (
  width: unknown,
  height: unknown
): boolean => {
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return true;
  }
  return Math.abs(w / h - 1) <= 0.001;
};

/**
 * EWW-453: the VISIBLE image area, i.e. `imageFrame` clamped to the canvas.
 *
 * `imageFrame` is the photo's rendered rect, which is NOT always inside the canvas:
 *  - contain-fit: the photo sits inside the canvas with margins, so the visible area is the
 *    photo's own rect (offsets positive, size smaller than the canvas)
 *  - cover-fit: the photo is larger than the canvas and cropped, so `offsetX`/`offsetY` go
 *    negative and a real save can report e.g. `width: 1201.8, offsetX: -262.9` on a 676-wide
 *    canvas -- a third of that rect is never visible and never exported
 *
 * Encoding a template against the raw frame is therefore only correct in the contain case; in
 * the cover case it references pixels that do not exist in the exported image. Clamping gives
 * the right answer for both: the photo rect when it fits, the canvas when it overflows.
 *
 * Returns null when there is no usable frame, so callers keep their existing fallback.
 */
export const getVisibleImageFrame = (
  imageFrame:
    | { width: number; height: number; offsetX?: number; offsetY?: number }
    | null
    | undefined,
  canvasWidth: number,
  canvasHeight: number
): { width: number; height: number; offsetX: number; offsetY: number } | null => {
  const fw = Number(imageFrame?.width);
  const fh = Number(imageFrame?.height);
  if (!Number.isFinite(fw) || !Number.isFinite(fh) || fw <= 0 || fh <= 0) return null;
  if (!(canvasWidth > 0) || !(canvasHeight > 0)) return null;

  const fx = Number(imageFrame?.offsetX) || 0;
  const fy = Number(imageFrame?.offsetY) || 0;

  const left = Math.max(0, fx);
  const top = Math.max(0, fy);
  const right = Math.min(canvasWidth, fx + fw);
  const bottom = Math.min(canvasHeight, fy + fh);

  const width = right - left;
  const height = bottom - top;
  if (!(width > 0) || !(height > 0)) return null;

  return { width, height, offsetX: left, offsetY: top };
};

// Builds next template name from highest numbered "template N" item.
export const getNextTemplateName = (
  templateArray: Array<{ name?: string }> = []
): string => {
  let highestNumber = 0;

  if (templateArray?.length > 0) {
    templateArray.forEach((template) => {
      const templateNumber = parseInt(
        String(template.name || "").split(" ")[1] || "0",
        10
      );
      if (!isNaN(templateNumber) && templateNumber > highestNumber) {
        highestNumber = templateNumber;
      }
    });
  }

  return `template ${highestNumber + 1}`;
};

/**
 * Template preview from the exported watermark layer.
 *
 * Default (genuine template authoring): unchanged -- centre-crop the longer side so the media is
 * a true 1:1, avoiding transparent padding.
 *
 * EWW-440/453 -- `imageFrame` given (saving an already-open image as a template): match iOS
 * exactly instead. There the preview is the watermarks drawn onto an empty canvas the size of the
 * IMAGE AREA, scaled down and stored as-is -- `prepareForMergeWithCanvasImage(bIsTemplate: true)`
 * followed by `scaleImageToSize`, with NO square crop anywhere
 * (CanvasScreenViewController.swift).
 *
 * Web's export is the same watermarks-only transparent PNG, but the size of the whole CANVAS.
 * Since EWW-453 the photo sits letterboxed inside that canvas and the watermarks are positioned
 * against the photo, so the watermarks occupy only that sub-rect: centre-cropping the full canvas
 * to a square leaves them small and off to one side. Cropping to the image frame -- and, like
 * iOS, not forcing a square -- reproduces iOS's preview.
 */
export const squareThumbnailPreview = async (
  downloadState: { Image: string },
  imageFrame?: {
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    canvasWidth: number;
    canvasHeight: number;
  } | null
) => {
  return new Promise<string>((resolve) => {
  const image = new window.Image();
  image.onerror = () => resolve(downloadState.Image);
  image.onload = () => {
    try {
      const naturalW = Math.max(1, image.naturalWidth || image.width || 1);
      const naturalH = Math.max(1, image.naturalHeight || image.height || 1);

      const draw = (
        sx: number,
        sy: number,
        sw: number,
        sh: number,
        outW: number,
        outH: number
      ) => {
        const canvas = document.createElement("canvas");
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(downloadState.Image);
          return;
        }
        ctx.clearRect(0, 0, outW, outH);
        ctx.drawImage(image, sx, sy, sw, sh, 0, 0, outW, outH);
        resolve(canvas.toDataURL("image/png"));
      };

      // iOS-matching path: the image area only, at its own aspect ratio.
      if (
        imageFrame &&
        Number(imageFrame.width) > 0 &&
        Number(imageFrame.height) > 0 &&
        Number(imageFrame.canvasWidth) > 0 &&
        Number(imageFrame.canvasHeight) > 0
      ) {
        // The export is the canvas rendered at pixelRatio 1, so canvas units map 1:1 to PNG
        // pixels -- but scale explicitly off the REAL canvas size rather than inferring it, so
        // this stays correct if that ever changes.
        const scaleX = naturalW / Number(imageFrame.canvasWidth);
        const scaleY = naturalH / Number(imageFrame.canvasHeight);
        const sx = Math.max(0, Math.round((Number(imageFrame.offsetX) || 0) * scaleX));
        const sy = Math.max(0, Math.round((Number(imageFrame.offsetY) || 0) * scaleY));
        const sw = Math.round(Number(imageFrame.width) * scaleX);
        const sh = Math.round(Number(imageFrame.height) * scaleY);
        // Only take this path when the frame genuinely lies inside the export; otherwise fall
        // through to the original behaviour rather than emitting a clipped/empty preview.
        if (sw > 0 && sh > 0 && sx + sw <= naturalW && sy + sh <= naturalH) {
          draw(sx, sy, sw, sh, sw, sh);
          return;
        }
      }

      const side = Math.min(naturalW, naturalH);
      const cropX = Math.max(0, Math.round((naturalW - side) / 2));
      const cropY = Math.max(0, Math.round((naturalH - side) / 2));
      draw(cropX, cropY, side, side, side, side);
    } catch {
      resolve(downloadState.Image);
    }
  };
  image.src = downloadState.Image;
})
};
